/**
 * Сервис синхронизации значений между кастомными и AMO полями
 * Обеспечивает двустороннюю синхронизацию с учетом типов полей
 */

import { 
    getCurrentAmoFieldValue, 
    setAmoFieldValue, 
    getAmoFieldValueText,
    debugAmoField
} from '../utils/amoFieldsUtils';
import { getFieldMapping } from './fieldMatchingService';
import { validateSafeSync, checkSyncConflicts } from './validationService';
import { 
    logSyncSuccess, 
    logSyncError, 
    logSyncWarning, 
    logSyncConflict,
    handleSyncException 
} from './syncLoggingService';
import { 
    showSyncFeedback,
    startSyncIndicator 
} from './syncNotificationService';

/**
 * Обратная синхронизация: вычисление значения из AMO для кастомного поля
 * Возвращает предложение для автозаполнения пустого кастомного поля на основе текущего значения AMO
 * @param {Object} customField
 * @returns {{ shouldBackfill: boolean, contentUpdate?: { value?: string, values?: string[] }, reason?: string }}
 */
export function computeBackfillForCustomField(customField) {
    try {
        if (!customField || customField.type === 'C') {
            return { shouldBackfill: false, reason: 'checkbox-or-empty' };
        }

        const mapping = getFieldMapping(customField);
        if (!mapping || !mapping.compatible || !mapping.amoField) {
            return { shouldBackfill: false, reason: 'no-mapping' };
        }

        const isCustomEmpty = (() => {
            if (customField.type === 'S') {
                const v = customField.content?.value;
                return v === undefined || v === null || v === '';
            }
            if (customField.type === 'M') {
                const vs = customField.content?.values;
                return !Array.isArray(vs) || vs.length === 0;
            }
            return true;
        })();

        if (!isCustomEmpty) {
            return { shouldBackfill: false, reason: 'custom-not-empty' };
        }

        const amoRaw = getCurrentAmoFieldValue(mapping.amoField.id);
        const amoText = getAmoFieldValueText(mapping.amoField.id, amoRaw);
        const amoHasValue = (() => {
            if (Array.isArray(amoRaw)) return amoRaw.length > 0;
            return Boolean(amoText && String(amoText).trim());
        })();

        if (!amoHasValue) {
            return { shouldBackfill: false, reason: 'amo-empty' };
        }

        const options = Array.isArray(customField.options) ? customField.options : [];

        const pickOption = (text) => {
            if (!text) return null;
            const norm = String(text).trim().toLowerCase();
            const found = options.find(o => String(o?.value ?? '').trim().toLowerCase() === norm);
            return found ? found.value : null; // вернуть исходное значение опции
        };

        const pickOptions = (texts) => {
            const set = new Set();
            texts.forEach(t => {
                const picked = pickOption(t);
                if (picked !== null && picked !== undefined && picked !== '') {
                    set.add(picked);
                }
            });
            return Array.from(set);
        };

        const splitText = (text) => String(text)
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

        const amoType = mapping.amoField.type; // 'SELECT' | 'MULTISELECT' | 'TEXT' | 'TEXTAREA'

        if (customField.type === 'S') {
            // селектор
            if (amoType === 'SELECT') {
                const picked = pickOption(amoText);
                if (picked) {
                    return { shouldBackfill: true, contentUpdate: { value: picked } };
                }
                return { shouldBackfill: false, reason: 'text-not-in-options' };
            } else if (amoType === 'MULTISELECT') {
                const texts = splitText(amoText);
                if (texts.length === 1) {
                    const picked = pickOption(texts[0]);
                    if (picked) {
                        return { shouldBackfill: true, contentUpdate: { value: picked } };
                    }
                }
                return { shouldBackfill: false, reason: 'multi-to-single-ambiguous' };
            } else if (amoType === 'TEXT' || amoType === 'TEXTAREA') {
                const picked = pickOption(amoText);
                if (picked) {
                    return { shouldBackfill: true, contentUpdate: { value: picked } };
                }
                return { shouldBackfill: false, reason: 'text-not-in-options' };
            }
        }

        if (customField.type === 'M') {
            // мультисписок
            if (amoType === 'MULTISELECT') {
                const texts = splitText(amoText);
                const picked = pickOptions(texts);
                if (picked.length > 0) {
                    return { shouldBackfill: true, contentUpdate: { values: picked } };
                }
                return { shouldBackfill: false, reason: 'no-overlap' };
            } else if (amoType === 'SELECT') {
                const picked = pickOption(amoText);
                if (picked) {
                    return { shouldBackfill: true, contentUpdate: { values: [picked] } };
                }
                return { shouldBackfill: false, reason: 'text-not-in-options' };
            } else if (amoType === 'TEXT' || amoType === 'TEXTAREA') {
                const texts = splitText(amoText);
                const picked = pickOptions(texts);
                if (picked.length > 0) {
                    return { shouldBackfill: true, contentUpdate: { values: picked } };
                }
                return { shouldBackfill: false, reason: 'no-overlap' };
            }
        }

        return { shouldBackfill: false, reason: 'unsupported-combination' };
    } catch (error) {
        console.error('Ошибка вычисления обратной синхронизации:', error);
        return { shouldBackfill: false, reason: 'exception' };
    }
}

/**
 * Проверка возможности синхронизации значения
 * @param {Object} mapping - Сопоставление поля
 * @param {*} customValue - Значение кастомного поля
 * @param {boolean} allowOverwrite - Разрешить перезапись существующего значения
 * @returns {Object} Результат проверки {canSync: boolean, reason?: string}
 */
export function canSyncValue(mapping, customValue, allowOverwrite = false) {
    try {
        if (!mapping || !mapping.compatible) {
            return { canSync: false, reason: 'Несовместимые типы полей' };
        }
        return { canSync: true };
    } catch (error) {
        console.error('Ошибка при проверке возможности синхронизации:', error);
        return { canSync: false, reason: 'Ошибка проверки' };
    }
}

/**
 * Синхронизация селектора с AMO селектором
 * @param {Object} customField - Кастомное поле
 * @param {string} customValue - Значение селектора
 * @param {boolean} allowOverwrite - Разрешить перезапись
 * @returns {Promise<Object>} Результат синхронизации
 */
export async function syncSelectToAmoSelect(customField, customValue, allowOverwrite = false) {
    try {
        const mapping = getFieldMapping(customField);
        if (!mapping) {
            return { success: false, error: 'Нет сопоставления с AMO полем' };
        }
        
        const canSync = canSyncValue(mapping, customValue, allowOverwrite);
        if (!canSync.canSync) {
            return { success: false, error: canSync.reason };
        }
        
        if (mapping.amoField.type === 'SELECT' || mapping.amoField.type === 'MULTISELECT') {
            
            if (!customValue || customValue.trim() === '') {
                const success = setAmoFieldValue(mapping.amoField.id, '');
                return { 
                    success, 
                    syncedValue: '',
                    message: success ? 'AMO селектор очищен' : 'Ошибка очистки поля'
                };
            }
            
            const amoEnumId = findMatchingEnumId(customValue, mapping.amoField.enums);
            
            if (amoEnumId) {
                const success = setAmoFieldValue(mapping.amoField.id, amoEnumId);
                const fieldTypeName = mapping.amoField.type === 'SELECT' ? 'селектором' : 'мультисписком';
                return { 
                    success, 
                    syncedValue: amoEnumId,
                    message: success ? `Значение синхронизировано с AMO ${fieldTypeName}` : 'Ошибка установки значения'
                };
            } else {
                const fieldTypeName = mapping.amoField.type === 'SELECT' ? 'селекторе' : 'мультисписке';
                return { success: false, error: `Значение "${customValue}" не найдено в AMO ${fieldTypeName}` };
            }
        } else if (mapping.amoField.type === 'TEXT' || mapping.amoField.type === 'TEXTAREA') {
            
            const success = setAmoFieldValue(mapping.amoField.id, customValue);
            return { 
                success, 
                syncedValue: customValue,
                message: success ? 'Значение синхронизировано с AMO текстовым полем' : 'Ошибка установки значения'
            };
        }
        
        return { success: false, error: 'Неподдерживаемый тип AMO поля' };
    } catch (error) {
        console.error('Ошибка синхронизации селектора:', error);
        return { success: false, error: 'Ошибка синхронизации' };
    }
}

/**
 * Синхронизация мультисписка с AMO мультисписком
 * @param {Object} customField - Кастомное поле
 * @param {Array} customValues - Значения мультисписка
 * @param {boolean} allowOverwrite - Разрешить перезапись
 * @returns {Promise<Object>} Результат синхронизации
 */
export async function syncMultiselectToAmoMultiselect(customField, customValues, allowOverwrite = false) {
    try {
        const mapping = getFieldMapping(customField);
        if (!mapping) {
            return { success: false, error: 'Нет сопоставления с AMO полем' };
        }
        
        const canSync = canSyncValue(mapping, customValues, allowOverwrite);
        if (!canSync.canSync) {
            return { success: false, error: canSync.reason };
        }
        
        if (mapping.amoField.type === 'MULTISELECT') {
            
            const amoEnumIds = [];
            const notFoundValues = [];
            
            customValues.forEach(value => {
                const enumId = findMatchingEnumId(value, mapping.amoField.enums);
                if (enumId) {
                    amoEnumIds.push(enumId);
                } else {
                    notFoundValues.push(value);
                }
            });
            
            
            const success = setAmoFieldValue(mapping.amoField.id, amoEnumIds);
            
            let message;
            if (customValues.length === 0) {
                message = 'AMO мультисписок очищен';
            } else if (amoEnumIds.length === 0) {
                message = 'Ни одно значение не найдено в AMO мультисписке, поле очищено';
            } else {
                message = notFoundValues.length > 0 
                    ? `Синхронизированы: ${amoEnumIds.length} значений. Не найдены: ${notFoundValues.join(', ')}`
                    : 'Все значения синхронизированы с AMO мультисписком';
            }
            
            return { 
                success, 
                error: success ? undefined : 'Значения найдены, но не удалось записать их в AMO мультисписок',
                syncedValue: amoEnumIds,
                message: success ? message : 'Ошибка записи значений в AMO мультисписок',
                notFound: notFoundValues
            };
        } else if (mapping.amoField.type === 'TEXT' || mapping.amoField.type === 'TEXTAREA') {
            
            const textValue = customValues.join(', ');
            const success = setAmoFieldValue(mapping.amoField.id, textValue);
            return { 
                success, 
                syncedValue: textValue,
                message: success ? 'Значения синхронизированы с AMO текстовым полем' : 'Ошибка установки значения'
            };
        }
        
        return { success: false, error: 'Неподдерживаемый тип AMO поля' };
    } catch (error) {
        console.error('Ошибка синхронизации мультисписка:', error);
        return { success: false, error: 'Ошибка синхронизации' };
    }
}

/**
 * Синхронизация с текстовым AMO полем
 * @param {Object} customField - Кастомное поле
 * @param {*} customValue - Значение (может быть строкой или массивом)
 * @param {boolean} allowOverwrite - Разрешить перезапись
 * @returns {Promise<Object>} Результат синхронизации
 */
export async function syncToAmoText(customField, customValue, allowOverwrite = false) {
    try {
        const mapping = getFieldMapping(customField);
        if (!mapping) {
            return { success: false, error: 'Нет сопоставления с AMO полем' };
        }
        
        
        let textValue = '';
        if (Array.isArray(customValue)) {
            textValue = customValue.join(', ');
        } else {
            textValue = String(customValue || '');
        }
        
        const canSync = canSyncValue(mapping, textValue, allowOverwrite);
        if (!canSync.canSync) {
            return { success: false, error: canSync.reason };
        }
        
        const success = setAmoFieldValue(mapping.amoField.id, textValue);
        return { 
            success, 
            syncedValue: textValue,
            message: success ? 'Значение синхронизировано с AMO текстовым полем' : 'Ошибка установки значения'
        };
    } catch (error) {
        console.error('Ошибка синхронизации с текстовым полем:', error);
        return { success: false, error: 'Ошибка синхронизации' };
    }
}

/**
 * Универсальная функция синхронизации кастомного поля
 * @param {Object} customField - Кастомное поле
 * @param {*} customValue - Значение поля
 * @param {boolean} allowOverwrite - Разрешить перезапись
 * @returns {Promise<Object>} Результат синхронизации
 */
export async function syncCustomFieldToAmo(customField, customValue, allowOverwrite = false) {
    
    const stopSyncIndicator = startSyncIndicator(customField.id);
    
    try {
        if (!customField || customField.type === 'C') {
            stopSyncIndicator();
            return { success: false, error: 'Чекбоксы не синхронизируются' };
        }
        
        
        const conflictCheck = checkSyncConflicts(customField, customValue);
        
        
        const finalAllowOverwrite = allowOverwrite || conflictCheck.canAutoReplace;
        
        const safetyCheck = validateSafeSync(customField, customValue, { 
            allowOverwrite: finalAllowOverwrite 
        });
        
        if (!safetyCheck.safe) {
            stopSyncIndicator();
            
            
            if (safetyCheck.requiresOverwrite && safetyCheck.conflict) {
                logSyncConflict(customField, customValue, safetyCheck.conflict);
                showSyncFeedback({
                    customField,
                    value: customValue,
                    status: 'conflict',
                    conflict: safetyCheck.conflict
                });
            } else {
                logSyncError(customField, customValue, safetyCheck.reason);
                showSyncFeedback({
                    customField,
                    value: customValue,
                    status: 'error',
                    message: safetyCheck.reason
                });
            }
            
            return { 
                success: false, 
                error: safetyCheck.reason,
                requiresOverwrite: safetyCheck.requiresOverwrite,
                conflict: safetyCheck.conflict
            };
        }
        
        
        let result;
        if (customField.type === 'S') {
            result = await syncSelectToAmoSelect(customField, customValue, finalAllowOverwrite);
        } else if (customField.type === 'M') {
            result = await syncMultiselectToAmoMultiselect(customField, customValue, finalAllowOverwrite);
        } else {
            stopSyncIndicator();
            return { success: false, error: 'Неподдерживаемый тип кастомного поля' };
        }
        
        stopSyncIndicator();
        
        
        if (result.success) {
            logSyncSuccess(customField, customValue, safetyCheck.mapping.amoField, result);
            
            
            showSyncFeedback({
                customField,
                value: customValue,
                status: 'success',
                amoField: safetyCheck.mapping.amoField,
                wasAutoReplaced: conflictCheck.canAutoReplace
            });
        } else {
            
            const amoFieldId = safetyCheck.mapping?.amoField?.id;
            if (amoFieldId) {
                debugAmoField(amoFieldId);
            }
            
            logSyncError(customField, customValue, result.error, {
                ...result,
                amoFieldId: safetyCheck.mapping?.amoField?.id,
                amoFieldName: safetyCheck.mapping?.amoField?.name
            });
            
            
            showSyncFeedback({
                customField,
                value: customValue,
                status: 'error',
                message: result.error
            });
        }
        
        
        if (result.warning || safetyCheck.warning) {
            logSyncWarning(customField, customValue, result.warning || safetyCheck.warning);
        }
        
        return result;
    } catch (error) {
        stopSyncIndicator();
        
        const result = handleSyncException(error, {
            customField: {
                id: customField?.id,
                name: customField?.name,
                type: customField?.type
            },
            customValue,
            allowOverwrite
        });
        
        
        showSyncFeedback({
            customField,
            value: customValue,
            status: 'error',
            message: result.error
        });
        
        return result;
    }
}



/**
 * Поиск ID enum по значению в AMO поле (только точное совпадение)
 * @param {string} value - Искомое значение
 * @param {Object} enums - Объект enum AMO поля
 * @returns {string|null} ID найденного enum или null
 */
function findMatchingEnumId(value, enums) {
    if (!value || !enums) return null;
    
    const normalizedValue = value.toLowerCase().trim();
    
    
    for (const [id, enumData] of Object.entries(enums)) {
        
        let enumValue = '';
        if (typeof enumData === 'string') {
            enumValue = enumData;
        } else if (enumData && typeof enumData === 'object') {
            enumValue = enumData.VALUE || enumData['~VALUE'] || '';
        }
        
        if (enumValue && enumValue.toLowerCase().trim() === normalizedValue) {
            return id;
        }
    }
    
    return null;
}

/**
 * Сравнение значений кастомного и AMO полей
 * @param {*} customValue - Значение кастомного поля
 * @param {*} amoValue - Значение AMO поля
 * @param {Object} mapping - Сопоставление полей
 * @returns {boolean} Равны ли значения
 */
function areValuesEqual(customValue, amoValue, mapping) {
    try {
        if (mapping.amoField.type === 'TEXT' || mapping.amoField.type === 'TEXTAREA') {
            const customText = Array.isArray(customValue) ? customValue.join(', ') : String(customValue || '');
            return customText === String(amoValue || '');
        }
        
        if (mapping.amoField.type === 'SELECT') {
            const amoText = getAmoFieldValueText(mapping.amoField.id, amoValue);
            return String(customValue || '') === amoText;
        }
        
        if (mapping.amoField.type === 'MULTISELECT') {
            const amoTexts = getAmoFieldValueText(mapping.amoField.id, amoValue).split(', ').filter(t => t);
            const customTexts = Array.isArray(customValue) ? customValue : [customValue].filter(v => v);
            
            if (amoTexts.length !== customTexts.length) return false;
            
            return customTexts.every(ct => amoTexts.some(at => at === ct));
        }
        
        return false;
    } catch (error) {
        console.error('Ошибка сравнения значений:', error);
        return false;
    }
}
